import { CurrencyView } from '../currency-view';
import { BlockLoading } from '../fetch-status/block-loading';
import { EndStatus } from '../fetch-status/end-status';
import { PositionNothing } from '../fetch-status/position-nothing';
import { LocalizedText } from '../localized-text';
import { OptionsPositionBadge } from '../options-positions-badge';
import { TextTooltip } from '../text-tooltip';
import { Button } from '../ui/button';
import { Separator } from '../ui/separator';
import { UploadedImage } from '../uploaded-image';
import { mergePendingPositions } from './merge-pending-positions';
import { IS_DEBUG, resolveBaseToken } from '@/client/confs/app';
import { useAuth } from '@/client/hooks/use-auth';
import { useWallet } from '@/client/hooks/use-wallet';
import { ShortRoundSubline } from '@/client/pages/btc-5m/short-round-subline';
import { useAppSettingStore } from '@/client/store/app-store';
import { useClaimStore } from '@/client/store/claim-store';
import { useModelStore } from '@/client/store/model-store';
import { usePendingPositionStore } from '@/client/store/pending-position-store';
import { useTradeParamsStore } from '@/client/store/trade-store';
import { cn } from '@/client/utils';
import { api } from '@/client/utils/api';
import { useUserPositions } from '@/libs/react-sdk/api/use-user-options';
import { PositionStatus, UserPositionInfo } from '@/libs/sdk/api';
import useInViewport from 'ahooks/lib/useInViewport';
import { BasicTarget } from 'ahooks/lib/utils/domTarget';
import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'wouter';

// Positions
export const PositionsItemDT = ({
  label,
  value,
  decimal = 6,
  isChange = false,
  baseTokenAddress,
}: {
  label: string;
  value: any;
  decimal?: number;
  isChange?: boolean;
  baseTokenAddress?: string;
}) => {
  return (
    <div className="flex flex-col">
      <div className="text-3 leading-[normal] font-medium text-foreground/40">{label}</div>
      <CurrencyView
        showIcon={true}
        isChange={isChange}
        value={value}
        decimal={decimal}
        precision={2}
        baseTokenAddress={baseTokenAddress}
        className="text-sm leading-[normal] font-medium"
      />
    </div>
  );
};

type PositionsItemProps = {
  className?: string;
  root?: BasicTarget<Element>;
  onViewChange?: (isInView: boolean) => void;
  uid: string;
  position: UserPositionInfo;
};

export const PositionsItem = ({ root, onViewChange, position, className }: PositionsItemProps) => {
  const { t } = useTranslation();
  const [, navigate] = useLocation();
  const ref = useRef<HTMLDivElement>(null);
  useInViewport(ref, {
    root,
    callback: entry => onViewChange?.(entry.isIntersecting),
  });
  const setWalletOpen = useModelStore(state => state.setWalletOpen);
  const setSellOpen = useModelStore(state => state.setSellOpen);
  const setSellParams = useModelStore(state => state.setSellParams);
  const setTradeOpen = useModelStore(state => state.setTradeOpen);
  const setTradeParams = useTradeParamsStore(state => state.setTradeParams);
  const baseToken = useAppSettingStore(state => state.baseToken);
  // Fallback when optionDecimal is 0 (missing option entity): prefer the
  // position's own base token decimals over the global lens (mixed rows)
  const decimal = position.optionDecimal || resolveBaseToken(position.baseTokenAddress)?.decimals || baseToken.decimals;
  // Multi-market events: event title as heading, market (team) name moves next
  // to the Yes/No badge so a bare position is identifiable.
  // Only promote the event title (and show market name as a chip) when we have
  // an event title; otherwise keep market name as the sole title to avoid
  // rendering the same name twice.
  const showEventTitle = (position.eventMarketCount ?? 1) > 1 && !!position.eventTitle;
  const titleText = showEventTitle ? position.eventTitle : position.marketName;
  // Market-content i18n maps travel with the row (backend name_i18n /
  // event_title_i18n); fall back to the EN base copy when the locale is absent.
  const titleI18nMap = showEventTitle ? position.eventTitleI18n : position.nameI18n;
  return (
    <div
      ref={ref}
      onClick={() => {
        setWalletOpen(false);
        navigate(`/event/${position.eventId}`);
      }}
      className={cn(
        'min-h-[137px] py-3 pl-4 pr-3 border border-foreground/10 rounded-4 hover:bg-foreground/5 cursor-pointer bg-card',
        className
      )}
    >
      <div className="flex items-center space-x-3">
        <UploadedImage fileKey={position.marketPicUrl} className="size-9 rounded-2" />
        <div className="space-y-1 flex-1">
          <TextTooltip
            tooltip={titleText}
            className="text-sm line-clamp-3 text-left leading-4.25 font-medium text-foreground market-title-hover"
          >
            <LocalizedText i18nMap={titleI18nMap} fallback={titleText ?? ''} />
          </TextTooltip>
          {/* 5-minute round positions only: window, baseline, final price and result */}
          <ShortRoundSubline eventId={position.eventId} title={[position.eventTitle, position.marketName]} />
          <div className="flex items-center space-x-1.5">
            {showEventTitle && (
              <span className="text-xs font-medium text-foreground/90 line-clamp-1 max-w-20">
                <LocalizedText i18nMap={position.nameI18n} fallback={position.marketName} />
              </span>
            )}
            <OptionsPositionBadge
              follow={false}
              options={[
                {
                  type: 'buy',
                  marketAddress: position.marketAddress,
                  label: position.optionDescription ?? position.optionName,
                  value: Number(position.balance),
                  decimal,
                  amount: BigInt(position.balance),
                },
              ]}
              onClick={e => {
                e.preventDefault();
                e.stopPropagation();
                setTradeOpen(true);
              }}
            />
          </div>
        </div>
        {/* <SharePosition uid={uid} position={position} decimal={decimal} /> */}
        {position.status === PositionStatus.using && (
          <Button
            variant="secondary"
            className="h-auto text-sm font-medium leading-5 py-1 px-4 border border-foreground/4 shadow-none"
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              setSellOpen(true);
              setSellParams({
                marketAddress: position.marketAddress,
                optionAddress: position.optionAddress,
              });
            }}
          >
            {t('sell')}
          </Button>
        )}
        {position.status != PositionStatus.using && (
          <Button
            variant="secondary"
            className="h-auto text-sm font-medium leading-5 py-1 px-4 border border-foreground/4 shadow-none"
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              setTradeOpen(true);
              setTradeParams({
                marketAddress: position.marketAddress,
                optionAddress: position.optionAddress,
                type: 'buy',
              });
            }}
          >
            {t('claim')}
          </Button>
        )}
      </div>
      <Separator orientation="vertical" className="h-[1px] w-full my-4 bg-foreground/4 " />
      <div className="grid grid-cols-3 grid-rows-2 gap-3">
        <PositionsItemDT
          baseTokenAddress={position.baseTokenAddress}
          label={t('entry-price')}
          value={position.entryPrice}
          decimal={decimal}
        />
        <PositionsItemDT
          baseTokenAddress={position.baseTokenAddress}
          label={t('market-price')}
          value={position.marketPirce}
          decimal={decimal}
        />
        <PositionsItemDT
          baseTokenAddress={position.baseTokenAddress}
          label={t('bet')}
          value={position.betValue}
          decimal={decimal}
        />
        <PositionsItemDT
          baseTokenAddress={position.baseTokenAddress}
          label={t('current')}
          value={position.currentValue}
          decimal={decimal}
        />
        <PositionsItemDT
          baseTokenAddress={position.baseTokenAddress}
          label={t('pnl')}
          value={position.pnl}
          isChange={true}
          decimal={decimal + 4}
        />
        <PositionsItemDT
          baseTokenAddress={position.baseTokenAddress}
          label={t('to-win')}
          value={position.toWin}
          decimal={decimal}
        />
      </div>
    </div>
  );
};

type PositionsProps = {
  uid: string;
  className?: string;
};
export const Positions = ({ uid, className }: PositionsProps) => {
  const claimedKeys = useClaimStore(state => state.claimedKeys);
  const { walletClient, chain } = useWallet();
  const auth = useAuth();
  const walletAddress = walletClient?.account?.address?.toLowerCase() ?? '';
  const baseToken = useAppSettingStore(state => state.baseToken);
  const { data, dataUpdatedAt, isLoading, hasNextPage, isFetchingNextPage, fetchNextPage } = useUserPositions(
    api,
    {
      page: 1,
      pageSize: 5,
      uid,
      baseTokenAddress: baseToken.address,
    },
    { enabled: auth.isAuthed && auth.data?.uid === uid }
  );
  const pending = usePendingPositionStore(state => state.pending);
  const prunePending = usePendingPositionStore(state => state.prunePending);
  // Only overlay pending buys that belong to the currently-viewed lens; a DPN
  // buy must not surface in the DPS list.
  const lensPending = useMemo(() => {
    const addr = baseToken.address.toLowerCase();
    return Object.fromEntries(
      Object.entries(pending).filter(([, p]) => (p.baseTokenAddress ?? addr).toLowerCase() === addr)
    );
  }, [pending, baseToken.address]);
  // Drop entries once the backend caught up (SSE-confirmed + list refetched) or
  // they expired — keyed on dataUpdatedAt so it runs after each list refresh.
  useEffect(() => {
    prunePending(dataUpdatedAt);
  }, [dataUpdatedAt, prunePending]);
  const positionList = useMemo(() => {
    if (!auth.isAuthed || auth.data?.uid !== uid) return [];
    const fetched =
      data?.pages
        .flatMap(item => item.positions)
        // Don't filter by DB isClaimed — chain state is the source of truth.
        // Backend already filters balance > 0; claim-store hides just-claimed positions in this session.
        .filter(item => !(walletAddress && claimedKeys.has(`${walletAddress}:${item.marketAddress.toLowerCase()}`))) ?? [];
    return mergePendingPositions(fetched, lensPending, { chainId: chain.id, account: walletAddress }, dataUpdatedAt);
  }, [data, claimedKeys, walletAddress, lensPending, dataUpdatedAt, chain.id, auth.isAuthed, auth.data?.uid, uid]);
  const moreLastIndex = useMemo(() => positionList.length - 2, [positionList.length]);
  const loadMoreItems = async (isInView: boolean, index: number) => {
    if (isInView && index === moreLastIndex && hasNextPage && !isFetchingNextPage) {
      if (IS_DEBUG) console.log('load more positions', uid, moreLastIndex, isInView, index);
      await fetchNextPage();
    }
  };
  return (
    <div className={cn('w-full', className)}>
      <div className="space-y-4">
        {positionList.map((item, index) => (
          <PositionsItem
            key={`positions-${index}`}
            uid={uid}
            position={item}
            onViewChange={isInView => loadMoreItems(isInView, index)}
          />
        ))}
      </div>
      {(isLoading || isFetchingNextPage) && <BlockLoading />}
      {!isLoading && !hasNextPage && positionList.length === 0 && <PositionNothing />}
      {!isLoading && !hasNextPage && positionList.length > 0 && <EndStatus />}
    </div>
  );
};
